import { useEffect, useState } from "react";
import { lesbar, tageZwischen, istUeberfaellig } from "./kalender";
import { ladeAufgabenDetails, ladeNamen, type Person } from "./laden";
import type { Aufgabe, AufgabenDetails, Gruppe } from "./planner";
import { mitGruppenfarbe } from "./stil";
import { PRIO_TEXT, PRIO_ZEICHEN, STATUS_TEXT } from "./texte";

// Detailfenster zur gewählten Aufgabe: rechts neben der Zeitleiste, am
// Telefon von unten.
//
// Zeitraum, Status und Priorität stehen schon in den Board-Daten. Notizen,
// Checkliste und die Namen der Bearbeiter kosten je Aufgabe eigene Anfragen –
// die laufen erst beim Anklicken, nicht für alle 53 Aufgaben im Voraus.

type Props = {
  aufgabe: Aufgabe;
  gruppe: Gruppe;
  heuteText: string;
  onSchliessen: () => void;
};

type Lage =
  | { art: "laedt" }
  | { art: "da"; details: AufgabenDetails; personen: Person[] }
  | { art: "fehler"; text: string };

export function Detail({ aufgabe: a, gruppe, heuteText, onSchliessen }: Props) {
  const [lage, setLage] = useState<Lage>({ art: "laedt" });

  useEffect(() => {
    // Wer schnell durch die Balken klickt, hat mehrere Anfragen gleichzeitig
    // unterwegs. Ohne diese Sperre könnte eine ältere Antwort die neuere
    // überschreiben – im Fenster stünden dann die Notizen der falschen Aufgabe.
    let abgebrochen = false;
    setLage({ art: "laedt" });
    (async () => {
      try {
        const [details, personen] = await Promise.all([
          ladeAufgabenDetails(a.id),
          ladeNamen(a.bearbeiterIds),
        ]);
        if (!abgebrochen) setLage({ art: "da", details, personen });
      } catch (fehler) {
        // Ein Fehler hier ist VORÜBERGEHEND und betrifft nur diesen Teil:
        // Zeitraum, Status und Priorität stehen weiter da, die Zeitleiste
        // dahinter bleibt unberührt.
        if (!abgebrochen) {
          setLage({ art: "fehler", text: fehler instanceof Error ? fehler.message : String(fehler) });
        }
      }
    })();
    return () => {
      abgebrochen = true;
    };
  }, [a.id, a.bearbeiterIds]);

  const einzeltermin = a.start === a.ende;
  const ueberfaellig = istUeberfaellig(a, heuteText);
  const tage = tageZwischen(a.start, a.ende);
  const erledigtePunkte = lage.art === "da" ? lage.details.checkliste.filter((p) => p.erledigt).length : 0;

  return (
    <aside className="detail" aria-label="Aufgabe">
      <header>
        <div>
          <span className="gruppe-tag" style={mitGruppenfarbe(gruppe.farbe)}>
            <i />
            {gruppe.name}
          </span>
          <h2>{a.titel}</h2>
        </div>
        <button type="button" className="zu" aria-label="Schließen" onClick={onSchliessen}>
          ×
        </button>
      </header>

      <dl>
        <dt>Status</dt>
        <dd>
          <span className={`status-pille ${a.status}`}>{STATUS_TEXT[a.status]}</span>
          {ueberfaellig && <span className="ueberfaellig-hinweis">Fälligkeit überschritten</span>}
        </dd>

        <dt>{einzeltermin ? "Datum" : "Zeitraum"}</dt>
        <dd>{einzeltermin ? lesbar(a.start) : `${lesbar(a.start)} – ${lesbar(a.ende)}`}</dd>

        <dt>Dauer</dt>
        <dd>{einzeltermin ? "Einzeltermin" : `${tage} Tage`}</dd>

        <dt>Priorität</dt>
        <dd>
          {PRIO_ZEICHEN[a.prioritaet] ? `${PRIO_ZEICHEN[a.prioritaet]} ` : ""}
          {PRIO_TEXT[a.prioritaet]}
        </dd>

        <dt>Zugewiesen</dt>
        <dd>
          {a.bearbeiterIds.length === 0 ? (
            <span className="platzhalter">niemand</span>
          ) : lage.art === "da" ? (
            lage.personen.map((p) => p.name).join(", ")
          ) : lage.art === "fehler" ? (
            <span className="platzhalter">nicht geladen</span>
          ) : (
            <span className="platzhalter">wird geladen …</span>
          )}
        </dd>
      </dl>

      <section>
        <h3>Notizen</h3>
        {lage.art === "laedt" && <p className="platzhalter">wird geladen …</p>}
        {lage.art === "fehler" && (
          <p className="platzhalter">Notizen und Checkliste konnten nicht geladen werden. ({lage.text})</p>
        )}
        {lage.art === "da" &&
          (lage.details.notizen ? (
            <p className="notizen">{lage.details.notizen}</p>
          ) : (
            <p className="platzhalter">keine</p>
          ))}
      </section>

      {lage.art === "da" && lage.details.checkliste.length > 0 && (
        <section>
          <h3>
            Checkliste {erledigtePunkte}/{lage.details.checkliste.length}
          </h3>
          <ul className="checkliste">
            {lage.details.checkliste.map((p) => (
              <li key={p.id} className={p.erledigt ? "erledigt" : ""}>
                <span className="kasten" aria-hidden="true">
                  {p.erledigt ? "✓" : ""}
                </span>
                <span>{p.titel}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <footer>Bearbeitet wird in dieser Stufe weiterhin in Planner.</footer>
    </aside>
  );
}
